import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

import {
  FEED_STALE_TIME,
  getCacheVersion,
  getEntry,
  runQuery,
  subscribeToCache,
} from "./queryCache";

const useQuery = ({
  key,
  tag,
  fetcher,
  enabled = true,
  staleTime = FEED_STALE_TIME,
}) => {
  const version = useSyncExternalStore(subscribeToCache, getCacheVersion);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const active = Boolean(enabled && key);

  useEffect(() => {
    if (!active) return;

    runQuery({
      key,
      tag,
      staleTime,
      fetcher: () => fetcherRef.current(),
    }).catch(() => {});
  }, [active, key, tag, staleTime, version]);

  const refetch = useCallback(() => {
    if (!key) return Promise.resolve(undefined);

    return runQuery({
      key,
      tag,
      staleTime,
      force: true,
      fetcher: () => fetcherRef.current(),
    }).catch(() => {});
  }, [key, tag, staleTime]);

  const entry = active ? getEntry(key) : undefined;

  return {
    currentData: entry?.data,
    error: entry?.error ?? null,
    isFetching: Boolean(entry?.promise),
    isSuccess: entry?.status === "success",
    isError: entry?.status === "error",
    enabled: active,
    refetch,
  };
};

export default useQuery;
